import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ComponentBase } from '../../../../../core/_infrastructure/classes/componentBase';
import { NotificationService } from '../../../../../core/_infrastructure/classes/services/notification.service';
import { AuthService } from '../../../../../core/_infrastructure/classes/services/auth.service';
import { ExceptionManager } from '../../../../../core/_infrastructure/classes/handlers/exceptionManager';
import { FormGroup, Validators } from '@angular/forms';
import { Tc } from '../../../../../core/_infrastructure/types/common';
import { DatePipe, DecimalPipe } from '@angular/common';
import { PeriodoCamViewModel } from '../periodocam.types';

@Component({
    selector: 'app-periodocam-edit',
    templateUrl: './periodocam-edit.component.html',
})
export class PeriodoCamEditComponent extends ComponentBase implements OnChanges, OnInit {
    @Input() periodoCam: PeriodoCamViewModel;
    @Input() formGroup: FormGroup;
    @Input() campanias: Tc[];
    @Input() esPeriodoFinalizado = false;
    @Input() habilitarEdicion: boolean;

    supMaxObligatoria = false;

    currentDate = new Date();

    isCollapsedCabecera = false;
    isCollapsedRequerimientos = false;
    isCollapsedUnidadInspeccion = false;

    constructor(
        public datepipe: DatePipe,
        public decimalPipe: DecimalPipe,
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.updateForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.periodoCam?.currentValue) {
            this.periodoCam = { ...changes.periodoCam.currentValue };
            this.updateForm();
        }
    }

    /**
     * This functions sets the values in the periodoCam (@Input) into the form.
     */
    private updateForm(): void {
        this.formGroup.setValue({
            Id: this.periodoCam?.Id || 0,
            FechaInicio: this.periodoCam?.FechaInicio ? new Date(this.periodoCam?.FechaInicio) : null,
            FechaFin: this.periodoCam?.FechaFin ? new Date(this.periodoCam?.FechaFin) : null,
            IdCampania: this.periodoCam?.IdCampania || null,
            DescripcionPeriodo: this.periodoCam?.DescripcionPeriodo || '',
            SuperficieMax: this.periodoCam?.SuperficieMax || null,
            Dextrio: this.decimalPipe.transform(this.periodoCam?.Dextrio, '1.2-2', 'es'),
            Parcela: this.periodoCam?.Parcela || false,
            Confeccion: this.periodoCam?.Confeccion || false,
            Invernadero: this.periodoCam?.Invernadero || false,
            EmpresasDeshidratacion: this.periodoCam?.EmpresasDeshidratacion || false,
            PresentarSolicitudesFueraPeriodo: this.periodoCam?.PresentarSolicitudesFueraPeriodo || false,
            ControlProduccionExportacion: this.periodoCam?.ControlProduccionExportacion || false,
            HabilitadaTemporalmente: this.periodoCam?.HabilitadaTemporalmente || false,
            CicloCortoInspParcela: this.periodoCam?.CicloCortoInspParcela || false,
            CicloCortoInspConfeccion: this.periodoCam?.CicloCortoInspConfeccion || false,
            CicloCortoInspInvernadero: this.periodoCam?.CicloCortoInspInvernadero || false,
            CicloCortoInspEmpresasDeshidratacion: this.periodoCam?.CicloCortoInspEmpresasDeshidratacion || false,
            RequiereFirmaInspectorPaisDestino: this.periodoCam?.RequiereFirmaInspectorPaisDestino || false,
            AvisoConfeccion: this.periodoCam?.AvisoConfeccion || false,
            RequiereAvisoFechaConfeccion: this.periodoCam?.RequiereAvisoFechaConfeccion || false,
            TamanioMaximoAgrupacion: this.periodoCam?.TamanioMaximoAgrupacion || false,
            AmbitoNacional: this.periodoCam?.AmbitoNacional || false, // Not visible but needed
            CertificaKgs: this.periodoCam?.CertificaKgs || false,
        });

        this.form = this.formGroup; // componentBase uses this.form
        this.onSelectedTamanioMax();
        this.onChangeParcela();
        this.onChangeConfeccion();
        this.onChangeInvernadero();
        this.onChangeEmpresasDeshidratacion();
    }

    onSelectedTamanioMax() {
        if (this.form.controls['TamanioMaximoAgrupacion'].value) {
            this.addValidator('SuperficieMax', Validators.required);
            this.supMaxObligatoria = true;
            if (!this.esPeriodoFinalizado && this.habilitarEdicion) {
                this.form.controls['SuperficieMax'].enable();
            }
        } else {
            this.form.patchValue({
                SuperficieMax: null,
            });
            this.removeValidators('SuperficieMax');
            this.form.controls['SuperficieMax'].disable();
            this.supMaxObligatoria = false;
        }
    }

    private manageCertificaKgs(): void {
        if (this.form.controls['Parcela'].value === true || this.form.controls['Invernadero'].value === true) {
            this.form.controls['CertificaKgs'].enable();
        }
        if (this.form.controls['Parcela'].value === false && this.form.controls['Invernadero'].value === false) {
            this.form.controls['CertificaKgs'].setValue(false);
            this.form.controls['CertificaKgs'].disable();
        }
    }

    onChangeParcela(): void {
        if (this.form.controls['Parcela'].value === false) {
            this.form.controls['CicloCortoInspParcela'].setValue(false);
            this.form.controls['CicloCortoInspParcela'].disable();
        } else if (!this.esPeriodoFinalizado && this.habilitarEdicion) {
            this.form.controls['CicloCortoInspParcela'].enable();
        }
        this.manageCertificaKgs();
    }

    onChangeConfeccion(): void {
        if (this.form.controls['Confeccion'].value === false) {
            this.form.controls['CicloCortoInspConfeccion'].setValue(false);
            this.form.controls['CicloCortoInspConfeccion'].disable();
        } else if (!this.esPeriodoFinalizado && this.habilitarEdicion) {
            this.form.controls['CicloCortoInspConfeccion'].enable();
        }
    }

    onChangeInvernadero(): void {
        if (this.form.controls['Invernadero'].value === false) {
            this.form.controls['CicloCortoInspInvernadero'].setValue(false);
            this.form.controls['CicloCortoInspInvernadero'].disable();
        } else if (!this.esPeriodoFinalizado && this.habilitarEdicion) {
            this.form.controls['CicloCortoInspInvernadero'].enable();
        }
        this.manageCertificaKgs();
    }

    onChangeEmpresasDeshidratacion(): void {
        if (this.form.controls['EmpresasDeshidratacion'].value === false) {
            this.form.controls['CicloCortoInspEmpresasDeshidratacion'].setValue(false);
            this.form.controls['CicloCortoInspEmpresasDeshidratacion'].disable();
        } else if (!this.esPeriodoFinalizado && this.habilitarEdicion) {
            this.form.controls['CicloCortoInspEmpresasDeshidratacion'].enable();
        }
    }

    get title(): string {
        return this.periodoCam ? 'Editar' : 'Nuevo'; // work mode: edition or creation
    }
}
